import {
  Home,
  User,
  Users,
  Heart,
  FileText,
  BarChart,
  Settings,
  UserCog,
  Hospital,
  Stethoscope,
  Plus,
  Globe,
  Shield,
  Baby,
} from 'lucide-react';
import { SectionType } from '@/contexts/SectionContext';
import { romanianLabels } from '@/utils/romanianLabels';

export interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  href?: string;
  badge?: string;
  children?: NavItem[];
  requiredLevel?: number; // 1=Maternity, 2=Hospital, 3=Administrator
}

// Prematuri section items - for hospital users working with premature patients
export const prematuriItems: NavItem[] = [
  {
    id: 'prematuri-dashboard',
    label: 'Tablou de bord',
    icon: Home,
    href: '/prematuri-dashboard',
  },
  {
    id: 'premature-patients',
    label: 'Copii prematuri',
    icon: Baby,
    href: '/premature-patients',
  },
];

// MCC section items - for hospital users working with MCC registry
export const chdItems: NavItem[] = [
  {
    id: 'chd-dashboard',
    label: 'Tablou de bord',
    icon: Home,
    href: '/chd-dashboard',
  },
  {
    id: 'new-case',
    label: 'Caz nou',
    icon: Plus,
    href: '/new-case',
  },
  {
    id: 'patients',
    label: romanianLabels.navigation.patients,
    icon: Users,
    href: '/patients',
  },
  {
    id: 'procedures',
    label: romanianLabels.navigation.procedures,
    icon: Stethoscope,
    href: '/procedures',
  },
  {
    id: 'diagnoses',
    label: romanianLabels.navigation.diagnoses,
    icon: Heart,
    href: '/diagnoses',
  },
  {
    id: 'reports',
    label: romanianLabels.navigation.reports,
    icon: FileText,
    href: '/reports',
    children: [
      {
        id: 'clinical-reports',
        label: 'Rapoarte clinice',
        icon: FileText,
        href: '/clinical-reports',
      },
      {
        id: 'hospital-reports',
        label: 'Rapoarte spital',
        icon: Hospital,
        href: '/reports-hospital',
      },
    ],
  },
];

// Common items for both sections (for hospital users)
export const commonItems: NavItem[] = [
  {
    id: 'profile',
    label: 'Profilul meu',
    icon: User,
    href: '/profile',
  },
];

// Maternity level items (level 1)
export const maternityItems: NavItem[] = [
  {
    id: 'maternity-dashboard',
    label: romanianLabels.navigation.dashboard,
    icon: Home,
    href: '/maternity-dashboard',
  },
  {
    id: 'profile',
    label: 'Profilul meu',
    icon: User,
    href: '/profile',
  },
  {
    id: 'new-case',
    label: 'Caz nou',
    icon: Plus,
    href: '/new-case',
    requiredLevel: 1,
  },
  {
    id: 'patients',
    label: romanianLabels.navigation.patients,
    icon: Users,
    href: '/patients',
    requiredLevel: 1,
  },
];

// Administrator level items (level 3) - full access
export const adminItems: NavItem[] = [
  {
    id: 'admin-dashboard',
    label: romanianLabels.navigation.dashboard,
    icon: Home,
    href: '/admin-dashboard',
  },
  {
    id: 'profile',
    label: 'Profilul meu',
    icon: User,
    href: '/profile',
  },
  {
    id: 'premature-patients',
    label: 'Copii prematuri',
    icon: Baby,
    href: '/premature-patients',
  },
  {
    id: 'new-case',
    label: 'Caz nou',
    icon: Plus,
    href: '/new-case',
  },
  {
    id: 'patients',
    label: romanianLabels.navigation.patients,
    icon: Users,
    href: '/patients',
  },
  {
    id: 'procedures',
    label: romanianLabels.navigation.procedures,
    icon: Stethoscope,
    href: '/procedures',
  },
  {
    id: 'diagnoses',
    label: romanianLabels.navigation.diagnoses,
    icon: Heart,
    href: '/diagnoses',
  },
  {
    id: 'reports',
    label: romanianLabels.navigation.reports,
    icon: FileText,
    href: '/reports',
    children: [
      {
        id: 'clinical-reports',
        label: 'Rapoarte clinice',
        icon: FileText,
        href: '/clinical-reports',
      },
      {
        id: 'hospital-reports',
        label: 'Rapoarte spital',
        icon: Hospital,
        href: '/reports-hospital',
      },
    ],
  },
  {
    id: 'admin',
    label: 'Administrare',
    icon: UserCog,
    requiredLevel: 3,
    children: [
      {
        id: 'users',
        label: romanianLabels.navigation.users,
        icon: Users,
        href: '/users',
      },
      {
        id: 'hospitals',
        label: 'Spitale',
        icon: Hospital,
        href: '/hospitals',
      },
      {
        id: 'procedure-types',
        label: 'Tipuri proceduri',
        icon: Stethoscope,
        href: '/procedure-types',
      },
      {
        id: 'chd-codes',
        label: 'Coduri MCC',
        icon: Heart,
        href: '/chd-codes',
      },
      {
        id: 'eurocat-reports',
        label: 'Rapoarte EUROCAT',
        icon: Globe,
        href: '/eurocat-reports',
      },
      {
        id: 'system-analytics',
        label: 'Analize sistem',
        icon: BarChart,
        href: '/system-analytics',
      },
      {
        id: 'audit',
        label: 'Audit',
        icon: Shield,
        href: '/audit',
      },
      {
        id: 'settings',
        label: 'Setări',
        icon: Settings,
        href: '/settings',
        requiredLevel: 3,
      },
    ],
  },
];

/**
 * Get navigation items based on user level and active section
 * @param userLevel - User's level (1=Maternity, 2=Hospital, 3=Administrator)
 * @param activeSection - Active section for hospital users (null for other levels)
 * @returns Array of navigation items to display
 */
export const getNavItemsForUser = (
  userLevel: number,
  activeSection: SectionType
): NavItem[] => {
  switch (userLevel) {
    case 1:
      // Maternity users - no section selection
      return maternityItems;

    case 2:
      // Hospital users - section-based navigation
      if (activeSection === 'prematuri') {
        return [...prematuriItems, ...commonItems];
      } else if (activeSection === 'chd') {
        return [...chdItems, ...commonItems];
      }
      // If no section selected, return empty (modal should show)
      return [];

    case 3:
      // Administrators - full access, no sections
      return adminItems;

    default:
      return [];
  }
};
